import { useCallback, useEffect } from 'react';
import { BranchCode } from 'api/risk';
import { FileTypeEnum } from 'api/documents';
import { useDocumentNotifications } from 'shared/signalR/hooks/useDocumentNotifications';
import { DocumentProgressDto } from 'shared/signalR/hooks/useDocumentNotifications';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import {
  useQuoteContractualizationData,
  useContractualizationActions
} from '../store';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useProcessedNotifications } from './useProcessedNotifications';

interface UseDocumentsSyncOptions {
  /** Called when documents are updated (for external refresh) */
  onDocumentsUpdated?: () => void;
}

/**
 * Hook centralisant la synchronisation des documents :
 * - Connexion SignalR pour les notifications temps reel
 * - Fetch initial des documents
 * - Mise a jour du store via notifications
 *
 * @example
 * ```tsx
 * const { isConnected } = useDocumentsSync();
 * ```
 */
export const useDocumentsSync = (options: UseDocumentsSyncOptions = {}) => {
  const { onDocumentsUpdated } = options;
  const quoteData = useQuoteContractualizationData();
  const { fetchSimulationDocuments, updateDocumentFromNotification } =
    useContractualizationActions();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const { t } = useDynamicTranslation([
    NsKey.Contractualization,
    NsKey.Document
  ]);

  const { memoizedConnect, memoizedDisconnect, notifications, latestNotification, isConnected } =
    useDocumentNotifications(quoteData?.quoteId);

  // Connexion SignalR
  useEffect(() => {
    memoizedConnect();
    return memoizedDisconnect;
  }, [memoizedConnect, memoizedDisconnect]);

  // Fetch initial des documents
  useEffect(() => {
    if (quoteData?.quoteId) {
      fetchSimulationDocuments(
        quoteData.quoteId,
        (quoteData.branchCode as BranchCode) || BranchCode.Unemployment
      );
    }
  }, [fetchSimulationDocuments, quoteData?.quoteId, quoteData?.branchCode]);

  // Refresh on notifications that don't carry a document (e.g. MandatSepa signed)
  // These are filtered out by useProcessedNotifications (no storedFileId),
  // so we react to latestNotification directly.
  useEffect(() => {
    if (latestNotification?.fileType === FileTypeEnum.MandatSepa) {
      refreshSubscriptionProcess();
      onDocumentsUpdated?.();
    }
  }, [latestNotification, refreshSubscriptionProcess, onDocumentsUpdated]);

  // Handler pour les nouvelles notifications (documents avec storedFileId)
  const handleNewNotification = useCallback(
    (notification: DocumentProgressDto) => {
      const label = notification.fileType ? t(notification.label) : '';
      updateDocumentFromNotification({
        ...notification,
        label
      });

      if (notification.fileType === FileTypeEnum.CertificatAdhesion) {
        refreshSubscriptionProcess();
      }

      onDocumentsUpdated?.();
    },
    [updateDocumentFromNotification, t, onDocumentsUpdated, refreshSubscriptionProcess]
  );

  // Utilisation du hook centralise pour la deduplication
  useProcessedNotifications(notifications, handleNewNotification);

  return {
    /** Whether SignalR is connected */
    isConnected,
    /** Manually refresh documents */
    refresh: useCallback(() => {
      if (quoteData?.quoteId) {
        fetchSimulationDocuments(
          quoteData.quoteId,
          (quoteData.branchCode as BranchCode) || BranchCode.Unemployment
        );
      }
    }, [fetchSimulationDocuments, quoteData?.quoteId, quoteData?.branchCode])
  };
};
